import React from "react"
import { Box, ButtonBase, Stack } from "@mui/material"
import { composeSx } from "../theme/composeSx"
import { colors, typeScale, typography } from "../theme/tokens"
import { VIRA_CARD_RADIUS } from "../atoms/ViraCard"
import { ViraText } from "../atoms/ViraText"

export type DayOfWeek = "Su" | "M" | "Tu" | "W" | "Th" | "F" | "Sa"

const DAYS: DayOfWeek[] = ["Su", "M", "Tu", "W", "Th", "F", "Sa"]

export type DayTabsProps = {
  selected?: DayOfWeek[]
  onChange?: (days: DayOfWeek[]) => void
  /** Optional field label above the control */
  label?: string
  required?: boolean
  disabled?: boolean
  /** Accessible name for the group (defaults to label or "Days of the week") */
  "aria-label"?: string
  fullWidth?: boolean
}

/**
 * Multi-select day-of-week control matching ContentTabs chrome.
 * Soft surface track, paper selected pills.
 */
export function DayTabs({
  selected = [],
  onChange,
  label,
  required = false,
  disabled = false,
  "aria-label": ariaLabel,
  fullWidth = true,
}: DayTabsProps) {
  const toggle = (day: DayOfWeek) => {
    if (!onChange || disabled) return
    if (selected.includes(day)) {
      onChange(selected.filter((d) => d !== day))
    } else {
      onChange([...selected, day])
    }
  }

  return (
    <Box>
      {label ? (
        <ViraText
          tone="ink"
          component="p"
          sx={{
            mb: 1,
            fontFamily: typography.fontFamily.body,
            fontSize: typeScale.body,
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          {label}
          {required ? (
            <Box component="span" sx={{ color: colors.secondary.crimson }}>
              *
            </Box>
          ) : null}
        </ViraText>
      ) : null}
      <Box
        role="group"
        aria-label={ariaLabel ?? label ?? "Days of the week"}
        aria-required={required || undefined}
        sx={(theme) => {
          const border = theme.palette.vira?.border ?? colors.vira.border
          const soft = theme.palette.vira?.softSurface ?? colors.vira.softSurface
          return {
            display: "inline-flex",
            width: fullWidth ? "100%" : "auto",
            maxWidth: "100%",
            boxSizing: "border-box",
            p: 0.5,
            gap: 0.5,
            borderRadius: VIRA_CARD_RADIUS,
            border: `1px solid ${border}`,
            backgroundColor: soft,
            overflowX: "clip",
            overflowY: "hidden",
          }
        }}
      >
        {DAYS.map((day) => {
          const isSelected = selected.includes(day)
          return (
            <ButtonBase
              key={day}
              aria-pressed={isSelected}
              disabled={disabled}
              onClick={() => toggle(day)}
              sx={composeSx((theme) => {
                const ink = theme.palette.vira?.ink ?? colors.vira.ink
                const muted = theme.palette.vira?.muted ?? colors.vira.muted
                const activeBg =
                  theme.palette.vira?.navActiveBg ?? colors.vira.navActiveBg
                const paper =
                  theme.palette.background?.paper ?? colors.neutral.white
                return {
                  flex: fullWidth ? "1 1 0" : "0 0 auto",
                  minWidth: 36,
                  minHeight: 40,
                  px: 1,
                  py: 1,
                  borderRadius: "8px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isSelected ? paper : "transparent",
                  boxShadow: isSelected
                    ? `inset 0 0 0 1px ${theme.palette.vira?.border ?? colors.vira.border}`
                    : "none",
                  color: isSelected ? ink : muted,
                  opacity: disabled ? 0.45 : 1,
                  "&:hover": {
                    backgroundColor: isSelected ? paper : activeBg,
                  },
                }
              })}
            >
              <Stack
                component="span"
                direction="row"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: "100%",
                  fontFamily: typography.fontFamily.display,
                  fontSize: typeScale.body,
                  fontWeight: isSelected ? 600 : 500,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.3,
                  textTransform: "none",
                }}
              >
                {day}
              </Stack>
            </ButtonBase>
          )
        })}
      </Box>
    </Box>
  )
}
